import { defineStore } from "pinia";
import { ref, shallowRef } from "vue";
import { storefront } from "../api/storefront";
import { ExperiencesEdgeClient, type PendingExperience } from "../cdp/experiences-client";
import { WebSdkTracker } from "../cdp/tracker";
import { pickRenderable, type RenderableExperience } from "../cdp/experience-view";
import { useChatStore } from "./chat";

/**
 * Client-side CDP foundation for the storefront (Фаза 2):
 *   • the browser Web SDK tracker (behavioral events by write-key), and
 *   • the on-site experiences overlay (pending + telemetry + survey submit).
 *
 * Both share ONE context from the BFF (`/api/v1/cdp/web-context`): a public
 * write-key + `pid:<profile>` ref + stable cookie_id. If the BFF has no write-key
 * (503) the whole feature self-disables — the storefront still works exactly as
 * before. The overlay component reads `current`; the tracking composable calls
 * `track()`.
 */
export const useCdpStore = defineStore("cdp", () => {
  const enabled = ref(false);
  const initialized = ref(false);
  const surface = ref("web");
  const current = ref<RenderableExperience | null>(null);
  const consentTopics = ref<string[]>([]);

  // Non-reactive singletons (network clients) — shallowRef so Vue doesn't proxy them.
  const edge = shallowRef<ExperiencesEdgeClient | null>(null);
  const tracker = shallowRef<WebSdkTracker | null>(null);

  const dismissed = new Set<string>();
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let nudgeTimer: ReturnType<typeof setTimeout> | null = null;
  let displayTimer: ReturnType<typeof setTimeout> | null = null;
  let recipientTimer: ReturnType<typeof setTimeout> | null = null;
  let pollInFlight: Promise<void> | null = null;
  let lifecycleGeneration = 0;

  async function init(): Promise<void> {
    if (initialized.value) return;
    const generation = lifecycleGeneration;
    initialized.value = true;
    try {
      const ctx = await storefront.cdp.webContext();
      if (generation !== lifecycleGeneration) return;
      if (!ctx?.writeKey) return; // disabled
      consentTopics.value = Array.isArray(ctx.consentTopics)
        ? ctx.consentTopics.filter((topic): topic is string => typeof topic === "string" && topic.length > 0)
        : [];
      let recipientToken: string | null = null;
      let recipientRefreshSeconds = 60;
      try {
        const auth = await storefront.cdp.experienceRecipientToken();
        recipientToken = auth.recipient_token;
        recipientRefreshSeconds = Math.max(30, auth.expires_in - 30);
      } catch {
        // Anonymous/unavailable authorization does not disable legacy experiences or tracking.
      }
      if (generation !== lifecycleGeneration) return;
      surface.value = ctx.surface || "web";
      edge.value = new ExperiencesEdgeClient({
        base: "/e",
        writeKey: ctx.writeKey,
        profileRef: ctx.profileRef,
        placement: ctx.placementCode,
        recipientToken,
        sessionId: getSessionId(),
        // Тестовый показ по ссылке из админки (?cdp_preview=…), баг-лист Насти п.1.
        previewToken: ExperiencesEdgeClient.readPreviewToken(),
        surveyBase: "",
      });
      tracker.value = new WebSdkTracker({ writeKey: ctx.writeKey, cookieId: ctx.cookieId });
      enabled.value = true;
      scheduleRecipientRefresh(recipientRefreshSeconds, generation);
      startPolling();
    } catch {
      if (generation !== lifecycleGeneration) return;
      // 503 (not configured) / network — feature stays off, storefront unaffected.
      enabled.value = false;
    }
  }

  function scheduleRecipientRefresh(afterSeconds: number, generation = lifecycleGeneration): void {
    if (recipientTimer) clearTimeout(recipientTimer);
    recipientTimer = setTimeout(async () => {
      recipientTimer = null;
      const client = edge.value;
      if (!client || generation !== lifecycleGeneration) return;
      try {
        const auth = await storefront.cdp.experienceRecipientToken();
        if (generation !== lifecycleGeneration || edge.value !== client) return;
        client.setRecipientToken(auth.recipient_token);
        scheduleRecipientRefresh(Math.max(30, auth.expires_in - 30), generation);
      } catch {
        if (generation !== lifecycleGeneration || edge.value !== client) return;
        client.setRecipientToken(null);
        scheduleRecipientRefresh(60, generation);
      }
    }, afterSeconds * 1000);
  }

  // Доказательство назначения + пометка тестового показа (такие события сервер не записывает).
  const assignmentProof = (view: RenderableExperience) => ({
    assignmentId: view.assignmentId ?? undefined,
    assignmentReceipt: view.assignmentReceipt ?? undefined,
    ...(view.kind !== "survey" && view.preview ? { preview: true } : {}),
  });
  const deliveryKey = (experienceId: string, assignmentId?: string | null) =>
    assignmentId ? `assignment:${assignmentId}` : `experience:${experienceId}`;

  function startPolling(intervalMs = 20_000): void {
    if (pollTimer) return;
    void poll();
    pollTimer = setInterval(() => void poll(), intervalMs);
  }

  function stopPolling(): void {
    if (pollTimer) clearInterval(pollTimer);
    pollTimer = null;
    if (nudgeTimer) clearTimeout(nudgeTimer);
    nudgeTimer = null;
    if (displayTimer) clearTimeout(displayTimer);
    displayTimer = null;
    pollInFlight = null;
  }

  async function poll(): Promise<void> {
    if (pollInFlight) return pollInFlight;
    const request = pollOnce();
    pollInFlight = request;
    try {
      await request;
    } finally {
      if (pollInFlight === request) pollInFlight = null;
    }
  }

  async function pollOnce(): Promise<void> {
    const generation = lifecycleGeneration;
    const client = edge.value;
    if (!client) return;
    // Keep showing the current experience until it's acted on (don't flap).
    if (current.value || displayTimer) return;
    try {
      const requestedSurface = surface.value;
      const list = await client.pending(requestedSurface, { fresh: true });
      if (generation !== lifecycleGeneration || edge.value !== client || current.value) return;
      const visible = list.filter((p) => {
        const key = deliveryKey(p.experience_id, p.assignment_id);
        if (dismissed.has(key) || isDismissedInCooldown(p.experience_id, p.client_rules)) return false;
        const consentTopic = typeof p.client_rules?.consent_topic === "string" ? p.client_rules.consent_topic : null;
        if (consentTopic && !consentTopics.value.includes(consentTopic)) return false;
        const cap = typeof p.session_cap === "number" && p.session_cap > 0 ? p.session_cap : null;
        return cap === null || sessionShownCount() < cap;
      });
      const next = pickRenderable(visible);
      // current.value is null here (guarded above), so any renderable is new.
      if (!next) return;
      const nextRules = "clientRules" in next ? next.clientRules : null;
      const delay = nextRules?.display?.type === "delay"
        ? Math.max(0, Math.min(86_400, Number(nextRules.display.delay_seconds) || 0))
        : 0;
      const show = () => {
        displayTimer = null;
        if (generation !== lifecycleGeneration || edge.value !== client || current.value) return;
        current.value = next;
        markSessionShown();
        void client.event({ surface: requestedSurface, experienceId: next.experienceId, type: "shown", ...assignmentProof(next) });
      };
      if (delay > 0) displayTimer = setTimeout(show, delay * 1000);
      else show();
    } catch {
      /* transient — try again next tick */
    }
  }

  /** Re-poll immediately (bypasses the "hold current" guard is unnecessary; caller ensures current is null). */
  function refreshSoon(): void {
    void poll();
  }

  /**
   * Debounced re-poll shortly after a behavioural event: an event-triggered
   * experience (offer on scroll_depth, directed survey render) only becomes
   * eligible AFTER the event is ingested, so a poll ~2.5s later surfaces it
   * without waiting for the next 20s interval tick. Debounced so a burst of
   * events (many product_impression) schedules at most one extra poll.
   */
  function nudge(): void {
    if (nudgeTimer) clearTimeout(nudgeTimer);
    nudgeTimer = setTimeout(() => {
      nudgeTimer = null;
      void poll();
    }, 2500);
  }

  function track(eventType: string, payload?: Record<string, unknown>): void {
    tracker.value?.track(eventType, payload);
    nudge();
  }

  function flush(): void {
    void tracker.value?.flush();
  }

  async function clickCurrentCta(): Promise<void> {
    const c = current.value;
    if (!c || !edge.value) return;
    await edge.value.event({ surface: surface.value, experienceId: c.experienceId, type: "clicked", ...assignmentProof(c) });
  }

  function dismissCurrent(): void {
    const c = current.value;
    if (!c) return;
    dismissed.add(deliveryKey(c.experienceId, c.assignmentId));
    const rules = "clientRules" in c ? c.clientRules : null;
    const seconds = typeof rules?.dismiss_cooldown_seconds === "number"
      ? Math.max(0, rules.dismiss_cooldown_seconds)
      : 0;
    if (seconds > 0) saveDismissed(c.experienceId, seconds);
    if (edge.value) void edge.value.event({ surface: surface.value, experienceId: c.experienceId, type: "dismissed", ...assignmentProof(c) });
    current.value = null;
    // Give the next eligible experience (if any) a chance to surface.
    refreshSoon();
  }

  function openChatFromCurrent(): Promise<boolean> {
    return useChatStore().init().then(() => useChatStore().open());
  }

  async function submitSurvey(
    view: Extract<RenderableExperience, { kind: "survey" }>,
    answers: { score: number; comment?: string | null; extra_answers?: Record<string, unknown> | null },
  ): Promise<{ ok: boolean; bucket?: string }> {
    const client = edge.value;
    if (!client) return { ok: false };
    const res = await client.submitSurvey(view.responseToken, answers);
    if (res.ok) {
      void client.event({ surface: surface.value, experienceId: view.experienceId, type: "submitted", ...assignmentProof(view) });
    }
    return { ok: res.ok, bucket: res.bucket };
  }

  function reset(): void {
    lifecycleGeneration += 1;
    stopPolling();
    current.value = null;
    dismissed.clear();
    if (recipientTimer) clearTimeout(recipientTimer);
    recipientTimer = null;
    edge.value?.setRecipientToken(null);
    edge.value = null;
    tracker.value = null;
    enabled.value = false;
    consentTopics.value = [];
    initialized.value = false;
  }

  function getSessionId(): string {
    try {
      const existing = globalThis.sessionStorage?.getItem("cdp_experience_session_id");
      if (existing) return existing;
      const created = globalThis.crypto?.randomUUID?.() ?? `sess_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      globalThis.sessionStorage?.setItem("cdp_experience_session_id", created);
      return created;
    } catch {
      return `sess_${Date.now()}`;
    }
  }

  function sessionShownCount(): number {
    try { return Number(globalThis.sessionStorage?.getItem("cdp_exp_shown_total") ?? 0) || 0; } catch { return 0; }
  }
  function markSessionShown(): void {
    try { globalThis.sessionStorage?.setItem("cdp_exp_shown_total", String(sessionShownCount() + 1)); } catch { /* storage unavailable */ }
  }
  function isDismissedInCooldown(experienceId: string, rules: PendingExperience["client_rules"]): boolean {
    try {
      const at = Number(globalThis.localStorage?.getItem(`cdp_exp_dismissed:${experienceId}`) ?? 0);
      if (!at) return false;
      const seconds = typeof rules?.dismiss_cooldown_seconds === "number" ? Math.max(0, rules.dismiss_cooldown_seconds) : 0;
      return Date.now() - at < seconds * 1000;
    } catch { return false; }
  }
  function saveDismissed(experienceId: string, seconds: number): void {
    try { globalThis.localStorage?.setItem(`cdp_exp_dismissed:${experienceId}`, String(Date.now())); } catch { /* storage unavailable */ }
    void seconds;
  }

  return {
    enabled,
    surface,
    current,
    init,
    poll,
    startPolling,
    stopPolling,
    track,
    flush,
    clickCurrentCta,
    dismissCurrent,
    openChatFromCurrent,
    submitSurvey,
    reset,
  };
});
